import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import { Tooltip } from '@/components/common/Tooltip';
import type { Order, OrderItemLotNumber } from '@/domain/Order';

const MAX_LENGTH_NAME = 25;
const MAX_LENGTH_STRENGTH = 10;
const MAX_LENGTH_LOT = 30;

const truncateName = (text: string): string => {
  if (text.length > MAX_LENGTH_NAME) {
    return `${text.slice(0, MAX_LENGTH_NAME)}...`;
  }
  return text;
};

const truncateStrength = (text: string): string => {
  if (text.length > MAX_LENGTH_STRENGTH) {
    return `${text.slice(0, MAX_LENGTH_STRENGTH)}...`;
  }
  return text;
};

const formatLotNumbers = (lotNumbers?: Array<OrderItemLotNumber>): string => {
  if (!lotNumbers?.length) return '-';
  return lotNumbers.map(({ lot, qty }) => `${lot} (${qty})`).join(', ');
};

interface OrderSupplementsTableProps {
  order: Order;
  showLotNumbers?: boolean;
}

export function OrderSupplementsTable({
  order,
  showLotNumbers = false,
}: OrderSupplementsTableProps) {
  return (
    <div className="bg-background mt-3 rounded-lg border p-6">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-slate-200 hover:bg-transparent">
            <TableHead className="bg-transparent py-2">Supplement</TableHead>
            <TableHead className="bg-transparent py-2">Dose</TableHead>
            {showLotNumbers && (
              <TableHead className="bg-transparent py-2">LOT</TableHead>
            )}
            <TableHead className="bg-transparent py-2 text-right">
              30 pills
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {order.items.map((item, index) => {
            const lotDisplay = formatLotNumbers(item.lot_numbers);

            return (
              <TableRow
                key={index}
                className="border-b border-slate-100 last:border-0 hover:bg-transparent"
              >
                <TableCell className="py-2">
                  <Tooltip
                    content={item.name || ''}
                    enabled={(item.name?.length || 0) > MAX_LENGTH_NAME}
                  >
                    <span>{item.name ? truncateName(item.name) : '-'}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className="py-2">
                  <Tooltip
                    content={item.strength || ''}
                    enabled={(item.strength?.length || 0) > MAX_LENGTH_STRENGTH}
                  >
                    <span>
                      {item.strength ? truncateStrength(item.strength) : '-'}
                    </span>
                  </Tooltip>
                </TableCell>
                {showLotNumbers && (
                  <TableCell className="py-2">
                    <Tooltip
                      content={lotDisplay}
                      enabled={lotDisplay.length > MAX_LENGTH_LOT}
                    >
                      <span>
                        {lotDisplay.length > MAX_LENGTH_LOT
                          ? `${lotDisplay.slice(0, MAX_LENGTH_LOT)}...`
                          : lotDisplay}
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell className="py-2 text-right">
                  {item.quantity || item.pills || '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
